import type { ConversationSummary } from '@axilon-apps/domains-ontology-ui-state'

const MS_PER_DAY = 86_400_000

export type ConversationGroupId = 'today' | 'yesterday' | 'last7days' | 'older'

export interface ConversationGroup {
  id: ConversationGroupId
  label: string
  conversations: ConversationSummary[]
}

const GROUP_ORDER: readonly ConversationGroupId[] = ['today', 'yesterday', 'last7days', 'older']

const GROUP_LABELS: Record<ConversationGroupId, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7days: 'Last 7 days',
  older: 'Older'
}

function startOfLocalDay(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/**
 * Bucket conversations by local calendar day of `updated_at`. Empty buckets
 * are omitted. Timestamps in the future (clock skew) stay in Today — they
 * must not create a group above it.
 */
export function groupConversationsByRecency(
  conversations: ConversationSummary[],
  now: Date = new Date()
): ConversationGroup[] {
  const startToday = startOfLocalDay(now).getTime()
  const startYesterday = startToday - MS_PER_DAY
  const startLast7 = startToday - 7 * MS_PER_DAY

  const buckets: Record<ConversationGroupId, ConversationSummary[]> = {
    today: [],
    yesterday: [],
    last7days: [],
    older: []
  }

  for (const conversation of conversations) {
    const updatedAt = Date.parse(conversation.updated_at)
    if (!Number.isFinite(updatedAt)) {
      buckets.older.push(conversation)
      continue
    }
    // Future timestamps (clock skew) stay in Today — never a group above it.
    if (updatedAt >= startToday) {
      buckets.today.push(conversation)
      continue
    }
    if (updatedAt >= startYesterday) {
      buckets.yesterday.push(conversation)
      continue
    }
    if (updatedAt >= startLast7) {
      buckets.last7days.push(conversation)
      continue
    }
    buckets.older.push(conversation)
  }

  return GROUP_ORDER.filter((id) => buckets[id].length > 0).map((id) => ({
    id,
    label: GROUP_LABELS[id],
    conversations: buckets[id]
  }))
}
